/**
 * Живые данные GitLab для форм DevOps: ветки и порядок репозиториев (DEV-1589).
 *
 * Зеркало трекера знает только то, что уже приехало вебхуком, а стенд поднимают
 * и для ветки, которую никто ещё не связал с задачей. Поэтому ветки читаются
 * живьём — своей ручкой сервиса.
 */
import { gitlabApi } from '@/api'

import { listRepositories } from './repositories'

/** Ветка репозитория в том виде, в каком её выбирают */
export interface Branch {
  name: string
  isDefault: boolean
  protected: boolean
  committedAt: string
}

/** Сколько открытых MR смотрим, чтобы понять, где сейчас идёт работа */
const RECENT_MERGE_REQUESTS = 50

export const listBranches = async (projectPath: string, search = ''): Promise<Branch[]> => {
  if (!projectPath) return []

  const { data } = await gitlabApi.gitLabServiceListBranches(projectPath, search || undefined)

  return (data.branches ?? []).map((branch) => ({
    name: branch.name ?? '',
    isDefault: branch.isDefault ?? false,
    protected: branch.protected ?? false,
    committedAt: branch.committedAt ?? '',
  }))
}

/**
 * Подключённые репозитории, сверху — те, где сейчас идёт работа.
 *
 * «Где идёт работа» берём из открытых merge request'ов: стенд почти всегда
 * поднимают для ветки, у которой MR уже открыт. Алфавитный список из сотни
 * путей заставлял бы искать свой руками каждый раз.
 *
 * Отказ ручки MR не роняет выбор: без подсказки останется тот же список,
 * просто в обычном порядке.
 */
export const listRepositoryPaths = async (): Promise<string[]> => {
  const { repositories } = await listRepositories('connected')
  const paths = repositories.filter((repo) => !repo.archived).map((repo) => repo.path)

  const recent = await recentPaths()
  const rank = new Map(recent.map((path, index) => [path, index]))

  return [...paths].sort((left, right) => {
    const leftRank = rank.get(left) ?? Number.MAX_SAFE_INTEGER
    const rightRank = rank.get(right) ?? Number.MAX_SAFE_INTEGER

    if (leftRank !== rightRank) return leftRank - rightRank

    return left.localeCompare(right)
  })
}

const recentPaths = async (): Promise<string[]> => {
  try {
    const { data } = await gitlabApi.gitLabServiceListMergeRequests(
      undefined,
      undefined,
      undefined,
      '1',
      String(RECENT_MERGE_REQUESTS),
    )

    const seen: string[] = []

    for (const request of data.mergeRequests ?? []) {
      const path = request.gitlabProjectPath ?? ''

      if (path && !seen.includes(path)) seen.push(path)
    }

    return seen
  } catch {
    return []
  }
}

/** Релиз репозитория: в этом проекте это семверный тег на master */
export interface Release {
  projectPath: string
  tag: string
  description: string
  author: string
  createdAt: string
  webUrl: string
  /**
   * Последняя сборка по этому тегу. Пусто — «сборки не знаем»: прогоны живут
   * в зеркале трекера ограниченный срок, и у старого тега их уже нет.
   */
  pipeline: { id: string; status: string; url: string; durationSeconds: number } | null
}

/**
 * Релизы подключённых репозиториев (DEV-1594).
 *
 * Раздел показывал журнал операций трекера — то есть только те релизы, что
 * собраны его же кнопкой. Тег, поставленный руками в GitLab, в историю не
 * попадал вовсе, и раздел врал про то, что выкачено.
 */
export const listReleases = async (projectPath = '', perRepository = 0): Promise<Release[]> => {
  const { data } = await gitlabApi.gitLabServiceListReleases(
    projectPath || undefined,
    perRepository || undefined,
  )

  return (data.releases ?? []).map((release) => ({
    projectPath: release.gitlabProjectPath ?? '',
    tag: release.tag ?? '',
    description: release.description ?? '',
    author: release.authorName ?? '',
    createdAt: release.createdAt ?? '',
    webUrl: release.webUrl ?? '',
    pipeline: release.pipelineStatus
      ? {
          id: release.pipelineId ?? '',
          status: release.pipelineStatus,
          url: release.pipelineWebUrl ?? '',
          durationSeconds: Number(release.pipelineDurationSeconds ?? 0),
        }
      : null,
  }))
}

/** Как сортируют историю релизов */
export type ReleaseOrder = 'newest' | 'oldest'

/**
 * Порядок истории. По умолчанию свежие сверху — раздел отвечает на вопрос
 * «что выкатили только что»; обратный порядок нужен, когда разбирают, с чего
 * началась цепочка выкатов.
 */
export const sortReleases = (releases: Release[], order: ReleaseOrder): Release[] => {
  const at = (release: Release) => new Date(release.createdAt).getTime() || 0

  return [...releases].sort((left, right) =>
    order === 'newest' ? at(right) - at(left) : at(left) - at(right),
  )
}

/** Группа истории: репозиторий и его релизы, свежие первыми */
export interface ReleaseGroup {
  path: string
  releases: Release[]
}

/**
 * Группировка по репозиториям. Порядок групп — по самому свежему релизу в
 * каждой: список отвечает на вопрос «что выкатывали недавно», и алфавит на
 * него не отвечает.
 */
export const groupByRepository = (releases: Release[]): ReleaseGroup[] => {
  const groups = new Map<string, Release[]>()

  for (const release of releases) {
    groups.set(release.projectPath, [...(groups.get(release.projectPath) ?? []), release])
  }

  return [...groups.entries()].map(([path, items]) => ({ path, releases: items }))
}
